<script lang="ts">
export default {
    name: "product-list-filter",
};
</script>

<template>
    <div data-role="product-list-filter-component" ref="div">
        <single-geolocation
            v-if="singleGeolocationFilter != null"
            :model="singleGeolocationFilter"
            :submit="submit"
        ></single-geolocation>
        <group-filter
            v-if="productGroup != null"
            :matched-logical-name="matchedLogicalName"
            :matched-logical-name-module-guid="matchedLogicalNameModuleGuid"
            :matched-logical-name-sub-module-guid="
                matchedLogicalNameSubModuleGuid
            "
            :matched-logical-name-entity-guid="matchedLogicalNameEntityGuid"
            :model="productGroup"
            :set-current-group="setCurrentGroup"
            :url-resolver="urlResolver"
            :submit="submit"
        ></group-filter>

        <collection-filter-comp
            v-if="collectionFilter != null && collectionFilter.enabled === true"
            ref="collectionFilterRef"
            :initialize-awaiters="initializeAwaiters"
            :matched-logical-name="matchedLogicalName"
            :matched-logical-name-module-guid="matchedLogicalNameModuleGuid"
            :matched-logical-name-sub-module-guid="
                matchedLogicalNameSubModuleGuid
            "
            :matched-logical-name-entity-guid="matchedLogicalNameEntityGuid"
            :get-product-collection-tree="getProductCollectionTree"
            :url-resolver="urlResolver"
            :product-list-guid="productListGuid"
            :model="collectionFilter"
            :form-is-loading="lazyFormIsLoading"
            :submit="submit"
            @input="setCollectionIdGroups($event)"
            @update:viewMode="setViewMode($event)"
        >
        </collection-filter-comp>

        <region-set-filter-comp
            v-if="regionSetFilter != null"
            v-show="shouldShowRegionSetFilter"
            ref="regionSetFilterComponent"
            :matched-logical-name="matchedLogicalName"
            :matched-logical-name-module-guid="matchedLogicalNameModuleGuid"
            :matched-logical-name-sub-module-guid="
                matchedLogicalNameSubModuleGuid
            "
            :matched-logical-name-entity-guid="matchedLogicalNameEntityGuid"
            :model="regionSetFilter"
            :url-resolver="urlResolver"
            :submit="submit"
        ></region-set-filter-comp>
    </div>
</template>

<script setup lang="ts">
import collectionFilterComp from "./collection-filter/collection-filter.vue";
import type { UrlResolver } from "./seo-url-parser";
import {
    type LoadingStateObserverData,
    getOrCreateObserverData,
} from "./loading-state-change";
import Vue, {
    type PropType,
    computed,
    nextTick,
    onMounted,
    ref,
    watch,
    getCurrentInstance,
    defineAsyncComponent,
    reactive,
} from "vue";
import {
    type GroupItemInfo,
    type RegionSetFilterInfo,
    type SimpleProductListQuery,
} from "./types";
import { type StoreItem } from "./product-list-state";
import { type getProductCollectionTree as getProductCollectionTreeFunction } from "./collection-filter/web-api";

const singleGeolocation = defineAsyncComponent(
    () =>
        import(
            /* webpackFetchPriority: "high" */
            "./SingleGeolocation.vue"
        )
);
const groupFilter = defineAsyncComponent(
    () =>
        import(
            /* webpackFetchPriority: "high" */
            "./new-product-group-view/NewProductGroupView.vue"
        )
);
const regionSetFilterComp = defineAsyncComponent(
    () =>
        import(
            /* webpackFetchPriority: "high" */
            "./region-set/RegionSetFilter.vue"
        )
);

const emit = defineEmits(["change-filter", "input"]);

function setCollectionIdGroups(collectionIdGroups: number[][] | undefined) {
    if (props.value == null) return;
    const copy = JSON.parse(
        JSON.stringify(props.value)
    ) as SimpleProductListQuery;
    copy.collectionIds = collectionIdGroups ?? [];
    emit("input", copy);
}

const localViewMode = ref<number>();

function setViewMode(n: number | undefined) {
    if (props.listState != null && props.listState.filter != null) {
        // console.debug("SETVIEWMODE:" + n);
        props.listState.filter.collectionViewMode = n;
        localViewMode.value = n;
    } else {
        console.warn("SETVIEWMODE:" + n + " didnt get set");
        localViewMode.value = n;
    }
}

// eslint-disable-next-line no-undef
const props = defineProps({
    urlResolver: {
        type: Object as PropType<UrlResolver>,
        required: true,
    },
    disableFormFeature: Boolean,
    productListGuid: Number,
    viewName: String,
    viewId: String,
    matchedLogicalName: String,
    matchedLogicalNameModuleGuid: Number,
    matchedLogicalNameSubModuleGuid: Number,
    matchedLogicalNameEntityGuid: Number,
    productGroup: Object,
    regionSetFilter: Object as PropType<RegionSetFilterInfo>,
    singleGeolocationFilter: Object,
    collectionFilter: Object as PropType<{
        collectionIdName: string;
        enabled: boolean;
        viewMode: number;
    }>,
    value: Object as PropType<SimpleProductListQuery>,
    listState: Object as PropType<StoreItem>,
    getProductCollectionTree: {
        type: Function as PropType<typeof getProductCollectionTreeFunction>,
    },
});

const shouldShowRegionSetFilter = computed(() => {
    return getShouldShowRegionSetFilter(
        props.regionSetFilter,
        currentGroup.value
    );
});
const lazyFormIsLoading = computed(() => {
    if (formIsLoading.value == null) return false;
    return formIsLoading.value.value;
});

const div = ref<HTMLDivElement>();

type regionSetFilterCompType = {
    reset(submit?: boolean): void;
};

const regionSetFilterComponent = ref();

const initializeAwaiters = ref<Promise<void>[]>([]);
const initTask = ref<Promise<Awaited<void[]>>>();
const currentGroup = ref<GroupItemInfo>();
const formIsLoading = ref<LoadingStateObserverData>();

watch(
    () => lazyFormIsLoading.value,
    (newValue) => {
        // console.debug("form is loading: " + newValue);
    }
);

function setCurrentGroup(group: GroupItemInfo) {
    const oldGroup = currentGroup.value;

    const shouldShowBefore = getShouldShowRegionSetFilter(
        props.regionSetFilter,
        oldGroup
    );
    const shouldShowAfter = getShouldShowRegionSetFilter(
        props.regionSetFilter,
        group
    );

    if (shouldShowBefore == true && shouldShowAfter == false) {
        // console.debug("CALLING TO RESET: START");
        (
            regionSetFilterComponent.value as unknown as regionSetFilterCompType
        )?.reset(false);
        // console.debug("CALLING TO RESET: DONE");
    }

    currentGroup.value = group;
}
function getShouldShowRegionSetFilter(
    regionSetFilter: RegionSetFilterInfo | null | undefined,
    currentGroup: GroupItemInfo | null | undefined
) {
    if (regionSetFilter == null) return false;
    if (regionSetFilter.hiddenByDefault) {
        if (currentGroup == null) return false;
        if (!shouldShowRegionFilterByGroup(currentGroup)) return false;
    }
    return true;
}
function shouldShowRegionFilterByGroup(group: GroupItemInfo) {
    if (group.hideRegionSetFilter !== true) return true;
    if (group.parent != null)
        return shouldShowRegionFilterByGroup(group.parent);
    return false;
}
function submit() {
    nextTick(function () {
        // console.log("emit change-filter");
        emit("change-filter");
    });
}

async function resolveUrlAndCall(func: () => void) {
    while (initTask.value == null) {
        console.warn("Did call resolve url before init task was started");
        await new Promise((res) => {
            requestAnimationFrame(() => {
                setTimeout(() => res(undefined), 100);
            });
        });
    }
    await initTask.value;
    await resolveUrl();
    func();
}

defineExpose({
    resolveUrlAndCall,
});

async function resolveUrl() {
    if (
        props.matchedLogicalName == null ||
        props.matchedLogicalNameModuleGuid == null ||
        props.matchedLogicalNameSubModuleGuid == null ||
        props.matchedLogicalNameEntityGuid == null
    ) {
        console.warn("cancelled resolving: no props data");
        return;
    }
    props.urlResolver.setMatchedLogicalData(
        props.matchedLogicalName,
        props.matchedLogicalNameModuleGuid,
        props.matchedLogicalNameSubModuleGuid,
        props.matchedLogicalNameEntityGuid
    );
    await props.urlResolver.parseCurrentPathName();
}
const instance = getCurrentInstance();
onMounted(async () => {
    //console.log("mount");
    if (props.listState != null) {
        props.listState.filter = reactive({
            resolveUrlAndCall(fnc) {
                resolveUrlAndCall(fnc);
            },
            submit() {
                submit();
            },
            collectionViewMode: localViewMode.value,
        });
    }
    if (div.value == null) throw new Error("missing el");
    div.value.setAttribute("data-vue-filter-component", "true");
    if ("$" in window && typeof window.$ === "function" && "fn" in window.$)
        window.$(div.value).data("vueComponent", instance?.proxy);

    if (props.disableFormFeature !== true)
        formIsLoading.value = getOrCreateObserverData(div.value, Vue);
    initTask.value = Promise.all(initializeAwaiters.value);
    await initTask.value;

    resolveUrlAndCall(function () {
        submit();
    });
});
</script>
